import { Component } from '@angular/core';
import { AuthenticationService } from './core/authentication.service';
import { IconRegistryService } from './core/icon-registry.service';
import { ConsentService } from './components/global/consent/consent.service';
import { BANNER_SLIDE_IN } from './components/global/consent/consent-banner/consent-banner.animation';
import { environment } from '../environments/environment';
import { ConsentBannerComponent } from './components/global/consent/consent-banner/consent-banner.component';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NavigationComponent } from './components/navigation/navigation.component';
import { NgClass } from '@angular/common';
import { LetDirective, PushPipe } from '@ngrx/component';
import { getAntdLocale } from './core/utils/antd-utils';
import { getDateFnsLocale } from './core/utils/date-fns-utils';
import { NzI18nService } from 'ng-zorro-antd/i18n';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getFromLocalStorage, isChristmasSeason, setToLocalStorage } from './core/utilities';
import { LocalStorageKeys } from './components/locations-skeleton/enums/local-storage-keys';
import { TOGGLE_NAVIGATION } from './nav-bar-animations';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { GxSnowfallDirective } from './directives/gx-snowfall.directive';
import { DateAdapter } from '@angular/material/core';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { FeedbackButtonComponent } from './components/global/feedback-button/feedback-button.component';
import { BehaviorSubject } from 'rxjs';
import { CATEGORIES } from './components/global/feedback-popup/user-feedback.service';
import { MainRoutes } from './core/enums';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [BANNER_SLIDE_IN,TOGGLE_NAVIGATION],
    standalone: true,
  imports: [
    LetDirective,
    NgClass,
    NavigationComponent,
    RouterOutlet,
    ConsentBannerComponent,
    MatIcon,
    MatTooltip,
    TranslateModule,
    GxSnowfallDirective,
    FeedbackButtonComponent,
    PushPipe,
  ],
})
export class AppComponent {
  consent$ = this.consentService.consent$;
  prod: boolean = environment.production;
  navigation: boolean;

  protected readonly isChristmasSeason = isChristmasSeason;

  private readonly showFeedbackButton = new BehaviorSubject(false);
  public readonly showFeedbackButton$ = this.showFeedbackButton.asObservable();

  constructor(
      public authService: AuthenticationService,
      private i18n: NzI18nService,
      private translate: TranslateService,
      private dateAdapter: DateAdapter<any>,
      private store: Store,
      // below 2 services are injected so that their constructor method is executed
      private iconRegistry: IconRegistryService,
      private consentService: ConsentService,
      private router: Router,
  ) {
    this.initNavigation();
    // update language of ANTD components / Material Date/Time pickers
    this.translate.onLangChange
        .subscribe(({ lang }) => {
          // global antd components' locale update
          this.i18n.setLocale(getAntdLocale(lang));

          // update the date-fns-specific translations
          // needed for long localized formats like `PP`
          // Here's the reason: https://ng.ant.design/docs/i18n/en#how-to-format-a-date-using-date-fns
          this.i18n.setDateLocale(getDateFnsLocale(lang));
          // Material
          this.dateAdapter.setLocale(getDateFnsLocale(lang));
        });
    // determine if FEEDBACK button should be shown (only on client-facing pages)
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          this.showFeedbackButton.next(
              CATEGORIES.includes(this.router.routerState?.snapshot?.root?.firstChild?.url[0]?.path),
          );
        });
  }

  /**
   * Init nav bar state from local storage
   */
  initNavigation() {
    const showNavigation = getFromLocalStorage(LocalStorageKeys.NAVIGATION);
    // null means no record in local storage therefore default back to 'true'
    this.navigation = showNavigation === null ? true : showNavigation;
  }

  /**
   * Toggle navigation side bar
   */
  toggleNavigation() {
    this.navigation = !this.navigation;
    setToLocalStorage(LocalStorageKeys.NAVIGATION, this.navigation);
  }

  /**
   * SHARED USER DEPRECATION WARNING functionality
   * Routes to /users and shows user account creation popup.
   */
  createAccount() {
    this.router.navigate([MainRoutes.USERS]).then(() => {
      // give some time to render view
      setTimeout(() => {
        const button = document.getElementById('new-user-button');
        button?.click();
      }, 250);
    });
  }
}
